import {
  Component, Input, ViewChild, ElementRef, Output,
  EventEmitter, ChangeDetectionStrategy, SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs';

@Component({
  selector: 'app-advanced-dropdown',
  templateUrl: './advanced-dropdown.component.html',
  styleUrls: ['./advanced-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedDropdownComponent {

  @ViewChild('dropdownContainer', { static: false }) dropdownContainer: ElementRef | undefined;
  @ViewChild('toggleButton') toggleButton: ElementRef | undefined;
  @ViewChild('items') items: ElementRef | undefined;
  @ViewChild('optionList') optionList?: ElementRef<HTMLElement>;
  private restoreScrollTop: number | null = null;

  @Input() selectSingle: boolean = false;
  @Input() displayKey: string = 'name';
  @Input() subArrayKey: string | undefined;
  @Input() options: any[];
  @Input() selectedOption: any[] | undefined;
  @Input() position: string = 'bottom';
  @Input() disabled: boolean = false;
  @Input() readonlyIds: string[] = [];
  @Input() placeholder: string = "";
  @Input() hideSelectedPills: boolean = false;
  @Input() config: AdvancedDropdownConfig | undefined;
  @Input() loading: boolean = false;
  @Input() hasMore: boolean = false;
  @Input() infiniteScroll: boolean = false;
  @Input() endOfResults: boolean = false;
  @Input() endOfResultsText: string = 'Đã hết dữ liệu';
  @Output() loadMore: EventEmitter<void> = new EventEmitter();

  @Output() selectedItem: EventEmitter<any> = new EventEmitter();
  @Output() selectedItemData: EventEmitter<any> = new EventEmitter();
  @Output() searchTerm: EventEmitter<any> = new EventEmitter();

  public isOpen = false;
  public optionsData: any[] = [];
  public selectedPills: any[] = [];
  public selectedIds: number[] = [];
  public breadCrumbValues: any[] = [];
  public term = new FormControl('');

  isReadonly(data: any): boolean {
    return this.disabled || this.readonlyIds.includes(data.id);
  }

  constructor() {}

  ngOnInit() {
    if (this.config?.apiSearch) {
      this.term.valueChanges.pipe(
        debounceTime(this.config?.debounceTime ?? 0),
      ).subscribe((data: any) => {
        this.searchTerm.emit(data ?? '');
      });
    }
    else {
      this.term.valueChanges.subscribe((data: any) => {
        if (data) {
          this.optionsData = [];
            this.options!.forEach(item => {
              this.hasValue(item) && this.optionsData.push(item)
            })
        } else {
          this.optionsData = this.options!;
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.disabled) this.isOpen = false;
    if (changes['options'] || changes['config']) {
      const oldOptions = changes['options']?.previousValue as any[] | undefined;
      if (this.infiniteScroll && oldOptions?.length && this.options?.length >= oldOptions.length &&
          oldOptions.every((option, index) => option.id === this.options[index]?.id)) {
        this.restoreScrollTop = this.optionList?.nativeElement.scrollTop ?? null;
      }
      this.optionsData = this.term.value && !this.config?.apiSearch
        ? (this.options || []).filter(item => this.hasValue(item)) : this.options || [];
    }
    if (!changes['selectedOption'] && !changes['options']) return;
    if (this.selectedOption?.length) {
      if (typeof this.selectedOption[0] === 'object' && this.selectedOption[0] !== null) {
        this.selectedPills = [...this.selectedOption];
        this.selectedIds = this.selectedOption.map((x: any) => x.id);
      } else {
        this.selectedIds = [...this.selectedOption];
        this.selectedPills = this.options ? this.options.filter((x: any) => this.selectedIds.includes(x.id)) : [];
      }
    } else {
      this.selectedPills = [];
      this.selectedIds = [];
    }
  }

  ngAfterViewChecked(): void {
    if (this.restoreScrollTop !== null && this.optionList) {
      const offset = this.restoreScrollTop;
      this.restoreScrollTop = null;
      this.optionList.nativeElement.scrollTop = offset;
    }
  }

  trackOption(index: number, option: any): string | number { return option.id ?? index; }

  getSelectedData(value: any) {
    this.options!.forEach(item => {
      this.recursiveSelected(item, value.map(function (x: any) {
        return x;
      }));
    })
  }

  recursiveSelected(item: any, value: any) {
    if (item.id && value.includes(item.id)) {
      this.onSelect(item)
    }
    if (item[this.subArrayKey!]?.length) {
      item[this.subArrayKey!].forEach((child: any) => {
        this.recursiveSelected(child, value)
      })
    }
  }

  hasValue(item: any) {
    let valueToReturn = false;
    if (item[this.displayKey].toLowerCase().includes(this.term?.value?.toLowerCase())) {
      valueToReturn = true;
    }
    item[this.subArrayKey!]?.length && item[this.subArrayKey!].forEach((child: any) => {
      if (this.hasValue(child)) {
        valueToReturn = true
      }
    })
    return valueToReturn
  }

  toggleDropdown(event: Event) {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      if (this.isOpen && this.config?.apiSearch) {
        this.searchTerm.emit(this.term?.value ?? '');
      }
    }
  }

  onSelect(data: any) {
    if (this.isReadonly(data)) return;
    if (this.selectSingle) {
      this.selectedPills = [];
      this.selectedIds = [];
      this.selectedPills.push(data);
      this.selectedIds.push(data.id);
      this.isOpen = false;
    }
    else {
      const index = this.selectedIds.indexOf(data.id);
      if (index < 0) {
        data.selected = true;
        this.selectedPills.push(data);
        this.selectedIds.push(data.id);
      } else {
        this.removeItem(data);
        return;
      }
    }

    this.selectedItem.emit(this.selectedIds);
    this.selectedItemData.emit(this.selectedPills);
  }

  removeItem(data: any) {
    if (this.isReadonly(data)) return;
    const index = this.selectedIds.indexOf(data.id);
    if (index >= 0) {
      this.selectedIds.splice(index, 1);
    }
    const pillIndex = this.selectedPills.findIndex((p: any) => p.id === data.id);
    if (pillIndex >= 0) {
      this.selectedPills.splice(pillIndex, 1);
    }
    this.selectedItem.emit(this.selectedIds);
    this.selectedItemData.emit(this.selectedPills);
  }

  subItemClicked(data: any) {
    if (this.disabled) return;
    this.isOpen = true;
    data[this.subArrayKey!]?.length && this.breadCrumbValues.push(data)
    data[this.subArrayKey!]?.length && (this.optionsData = data[this.subArrayKey!])
  }

  changeTo(data: any) {
    if (this.disabled) return;
    if (this.subArrayKey) {
      this.optionsData = data[this.subArrayKey]
      this.breadCrumbValues.splice(this.breadCrumbValues.indexOf(data) + 1, this.breadCrumbValues.length - this.breadCrumbValues.indexOf(data))
    }
  }

  clearOptions() {
    if (this.disabled) return;
    const preserved = this.selectedPills.filter(item => this.readonlyIds.includes(item.id));
    if (preserved.length) {
      const allSelected = this.options.every(item => this.selectedIds.includes(item.id));
      const selected = allSelected ? [] : this.options;
      this.selectedPills = Array.from(new Map([...preserved, ...selected].map(item => [item.id, item])).values());
      this.selectedIds = this.selectedPills.map(item => item.id);
      this.selectedItem.emit(this.selectedIds);
      this.selectedItemData.emit(this.selectedPills);
      return;
    }
    if (JSON.stringify(this.selectedPills) == JSON.stringify(this.options)) {
      this.selectedPills = [];
      this.selectedIds = [];
      this.selectedItem.emit(this.selectedIds);
      this.selectedItemData.emit(this.selectedPills);
    }
    else {
      this.selectedPills = this.options;
      this.selectedIds = this.options.map((item: any) => item.id);
      this.selectedItem.emit(this.selectedIds);
      this.selectedItemData.emit(this.selectedPills);

    }
    // this.optionsData = this.options!;
    // this.breadCrumbValues = [];
  }

  clickOutside(): void {
    this.isOpen = false;
  }

  onOptionsScroll(event: Event): void {
    const list = event.target as HTMLElement;
    if (this.infiniteScroll && this.hasMore && !this.loading &&
        list.scrollHeight - list.scrollTop - list.clientHeight <= 240) {
      this.loadMore.emit();
    }
  }

}

export class AdvancedDropdownConfig{
  hideSelectAll?: boolean = false;
  isSearchable?: boolean;
  apiSearch?: boolean = true; // search from api
  debounceTime?: number = 300;
}
